import * as React from "react";
import UnsavedChangesConfirm from "../components/dialog/UnsavedChangesConfirm";

export type UseUnsavedChangesConfirmOptions = {
  title?: string;
  description?: string;
  onSave?: () => void | Promise<void>;
  onDiscard?: () => void | Promise<void>;
  onSaveLoading: boolean;
};

export type UseUnsavedChangesConfirmReturn = {
  Confirm: React.ReactElement | null;
  setDirty: (dirty: boolean) => void;
  markDirty: () => void;
  clearDirty: () => void;
  isDirty: boolean;
  guard: (proceed: () => void) => void;
  prompt: () => Promise<"save" | "discard" | "cancel" | "noop">;
};

export function useUnsavedChangesConfirm(
  opts: UseUnsavedChangesConfirmOptions
): UseUnsavedChangesConfirmReturn {
  const {
    title = "You have unsaved changes",
    description = "You have unsaved changes in this sidebar. What would you like to do?",
    onSave,
    onDiscard,
    onSaveLoading,
  } = opts;

  const [isDirty, setIsDirty] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const proceedRef = React.useRef<null | (() => void)>(null);

  const setDirty = React.useCallback((dirty: boolean) => setIsDirty(dirty), []);
  const markDirty = React.useCallback(() => setIsDirty(true), []);
  const clearDirty = React.useCallback(() => setIsDirty(false), []);

  const closeDialog = React.useCallback(() => setOpen(false), []);

  const guard = React.useCallback(
    (proceed: () => void) => {
      if (!isDirty) {
        proceed();

        return;
      }

      proceedRef.current = proceed;

      setOpen(true);
    },
    [isDirty]
  );

  const handleDiscard = React.useCallback(async () => {
    try {
      if (onDiscard) await onDiscard();
    } finally {
      clearDirty();

      closeDialog();

      if (proceedRef.current) {
        const fn = proceedRef.current;

        proceedRef.current = null;

        fn();
      }
    }
  }, [onDiscard, clearDirty, closeDialog]);

  const handleSave = React.useCallback(async () => {
    try {
      if (onSave) await onSave();

      clearDirty();

      closeDialog();

      if (proceedRef.current) {
        const fn = proceedRef.current;

        proceedRef.current = null;

        fn();
      }
    } catch (e) {
      console.error(e);
    }
  }, [onSave, clearDirty, closeDialog]);

  const handleCancel = React.useCallback(() => {
    proceedRef.current = null;

    closeDialog();
  }, [closeDialog]);

  const prompt = React.useCallback(async (): Promise<
    "save" | "discard" | "cancel" | "noop"
  > => {
    if (!isDirty) return "noop";

    return new Promise(resolve => {
      proceedRef.current = () => resolve("discard");

      setOpen(true);
    });
  }, [isDirty]);

  const Confirm = open ? (
    <UnsavedChangesConfirm
      open={open}
      title={title}
      description={description}
      onDiscard={handleDiscard}
      onCancel={handleCancel}
      onSave={handleSave}
      onSaveLoading={onSaveLoading}
    />
  ) : null;

  return { Confirm, setDirty, markDirty, clearDirty, isDirty, guard, prompt };
}
